import React from 'react';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { PhoneIcon, MailIcon, CalendarIcon, MoreHorizontalIcon, DownloadIcon, PlusIcon, SearchIcon } from 'lucide-react';
export interface Lead {
  id: string;
  name: string;
  phone: string;
  email: string;
  status: 'New' | 'Contacted' | 'Qualified' | 'Bad Number';
  lastContact: string;
  loanAmount: string;
  creditScore: number;
  source: string;
}
interface LeadListTableProps {
  leads: Lead[];
  campaignName: string;
}
export function LeadListTable({
  leads,
  campaignName
}: LeadListTableProps) {
  return <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden h-full flex flex-col" data-id="element-35">
      <div className="px-6 py-5 border-b border-gray-200 flex justify-between items-center bg-white" data-id="element-36">
        <div data-id="element-37">
          <h3 className="text-lg font-bold text-gray-900 tracking-tight" data-id="element-38">
            {campaignName}
          </h3>
          <p className="text-sm text-gray-500 mt-0.5" data-id="element-39">
            Showing {leads.length} qualified leads
          </p>
        </div>
        <div className="flex gap-3" data-id="element-40">
          <div className="relative hidden sm:block" data-id="element-41">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-42" />
            <input type="text" placeholder="Filter leads..." className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-48" data-id="element-43" />
          </div>
          <Button variant="secondary" size="sm" className="hidden sm:flex" data-id="element-44">
            <DownloadIcon className="w-4 h-4 mr-2" data-id="element-45" />
            Export
          </Button>
          <Button variant="primary" size="sm" data-id="element-46">
            <PlusIcon className="w-4 h-4 mr-2" data-id="element-47" />
            Add Lead
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto flex-1" data-id="element-48">
        <table className="w-full text-left text-sm" data-id="element-49">
          <thead className="bg-gray-50/50 text-gray-500 font-semibold border-b border-gray-200" data-id="element-50">
            <tr data-id="element-51">
              <th className="px-6 py-4 text-xs uppercase tracking-wider" data-id="element-52">
                Lead Details
              </th>
              <th className="px-6 py-4 text-xs uppercase tracking-wider" data-id="element-53">
                Status
              </th>
              <th className="px-6 py-4 text-xs uppercase tracking-wider" data-id="element-54">
                Contact Info
              </th>
              <th className="px-6 py-4 text-xs uppercase tracking-wider" data-id="element-55">
                Loan Profile
              </th>
              <th className="px-6 py-4 text-xs uppercase tracking-wider" data-id="element-56">
                Last Activity
              </th>
              <th className="px-6 py-4 text-right text-xs uppercase tracking-wider" data-id="element-57">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white" data-id="element-58">
            {leads.map(lead => <tr key={lead.id} className="hover:bg-blue-50/30 transition-colors group" data-id="element-59">
                <td className="px-6 py-4" data-id="element-60">
                  <div className="font-semibold text-gray-900" data-id="element-61">{lead.name}</div>
                  <div className="text-xs text-gray-400 font-mono mt-0.5" data-id="element-62">
                    ID: {lead.id}
                  </div>
                </td>
                <td className="px-6 py-4" data-id="element-63">
                  <Badge variant={lead.status === 'Qualified' ? 'success' : lead.status === 'New' ? 'blue' : lead.status === 'Bad Number' ? 'error' : 'neutral'} data-id="element-64">
                    {lead.status}
                  </Badge>
                </td>
                <td className="px-6 py-4" data-id="element-65">
                  <div className="flex flex-col gap-1.5" data-id="element-66">
                    <div className="flex items-center text-gray-600 text-xs group-hover:text-blue-700 transition-colors" data-id="element-67">
                      <PhoneIcon className="w-3.5 h-3.5 mr-2 text-gray-400 group-hover:text-blue-500" data-id="element-68" />
                      {lead.phone}
                    </div>
                    <div className="flex items-center text-gray-600 text-xs" data-id="element-69">
                      <MailIcon className="w-3.5 h-3.5 mr-2 text-gray-400" data-id="element-70" />
                      {lead.email}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4" data-id="element-71">
                  <div className="text-gray-900 font-semibold" data-id="element-72">
                    {lead.loanAmount}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5" data-id="element-73">
                    <span className="font-medium text-gray-700" data-id="element-74">
                      {lead.creditScore}
                    </span>{' '}
                    FICO • {lead.source}
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-600" data-id="element-75">
                  <div className="flex items-center" data-id="element-76">
                    <CalendarIcon className="w-4 h-4 mr-2 text-gray-400" data-id="element-77" />
                    <span className="text-xs" data-id="element-78">{lead.lastContact}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-right" data-id="element-79">
                  <button className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-80">
                    <MoreHorizontalIcon className="w-5 h-5" data-id="element-81" />
                  </button>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-center" data-id="element-82">
        <Button variant="ghost" size="sm" className="text-gray-500 hover:text-gray-900" data-id="element-83">
          View All Leads
        </Button>
      </div>
    </div>;
}